import React from 'react';
import PropTypes from 'prop-types';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import AppDialog from '../Dialog/AppDialog';
import clsx from 'clsx';
import useWidth from '../Shared/useWidth';
import { isWidthUp } from '@material-ui/core/withWidth';

const useStyles = makeStyles((theme) => ({
  root: {
    top: 'auto',
    bottom: 0,
    backgroundColor: theme.props.backgroundColor.primary.main,
  },
  toolbar: {
    justifyContent: 'center',
    padding: theme.spacing(2),
    flexDirection: 'column',
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      '& > *:not(:last-child)': {
        marginRight: theme.spacing(2),
        marginBottom: 0,
      },
    },
  },
  button: {
    padding: theme.spacing(1.5),
    width: '100%',
    maxWidth: 300,
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
    '&.weight': {
      fontWeight: 'bold',
    },
    '&.underline': {
      textDecoration: 'underline',
    },
  },
}));

const Action = ({
  ButtonProps: {
    className: buttonClassName,
    onClick,
    variant,
    ...otherButtonProps
  } = {},
  DialogComponent = AppDialog,
  DialogProps,
  classes,
}) => {
  const dialogRef = React.useRef();
  const width = useWidth();
  const handleOpen = () => dialogRef.current?.handleOpen();
  return (
    <>
      <Button
        size={isWidthUp('sm', width) ? 'large' : 'small'}
        variant={variant}
        {...otherButtonProps}
        className={clsx(
          classes.button,
          buttonClassName,
          variant === 'contained' && 'weight',
          (variant === 'text' || !variant) && 'underline',
        )}
        onClick={() => {
          if (onClick) {
            onClick(handleOpen);
          } else {
            handleOpen();
          }
        }}
      />
      {React.createElement(DialogComponent, { ...DialogProps, ref: dialogRef })}
    </>
  );
};

const ActionBar = ({
  actions = [],
  selector = 'body',
  classes: overridableClasses,
}) => {
  const classes = useStyles({ classes: overridableClasses });
  const appBarRef = React.useRef();

  const setPadding = () => {
    if (!appBarRef.current) return;
    const target = document.querySelector(selector);
    target?.style?.setProperty(
      'padding-bottom',
      `${appBarRef.current.scrollHeight}px`,
    );
  };

  const removePadding = () => {
    const target = document.querySelector(selector);
    target?.style?.removeProperty('padding-bottom');
  };

  React.useEffect(() => {
    setPadding();
    window.addEventListener('resize', setPadding);
    return () => {
      removePadding();
      window.removeEventListener('resize', setPadding);
    };
  }, []);

  return (
    <AppBar position="fixed" className={classes.root} ref={appBarRef}>
      <Toolbar className={classes.toolbar}>
        {actions.map((actionProps, index) => (
          <Action key={`action${index}`} classes={classes} {...actionProps} />
        ))}
      </Toolbar>
    </AppBar>
  );
};

ActionBar.propTypes = {
  selector: PropTypes.string,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      ButtonProps: PropTypes.object,
      DialogComponent: PropTypes.object,
      DialogProps: PropTypes.object,
    }),
  ),
};

export default ActionBar;
